import { useCallback, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
    getActiveAssignmentConflictMessage,
    getAssignmentLimitMessage,
    getFocusUpdateRequiredMessage,
    getPriorityAuthzMessage,
} from "@/lib/priority-errors";
import { priorityService } from "@/services/priorityService";
import { ICreatePriority, IUpdatePriority, Priority } from "@/types";
import type { PriorityFormValues } from "./priority-form-schema";
import { COMMIT_GATE_MESSAGE, type SubmitIntent } from "./priority-form-constants";
import { toAssignmentPayload } from "./priority-form-transforms";

interface UsePriorityFormSubmitArgs {
    form: UseFormReturn<PriorityFormValues>;
    initialData?: Partial<Priority>;
    isOwnRowOnly: boolean;
    canEditTask: boolean;
    canEditOwnRow: boolean;
    driName?: string;
    week: number;
    year: number;
    onSubmitRef?: (submitFn: (intent?: SubmitIntent) => void) => void;
    onSubmittingChange?: (isSubmitting: boolean) => void;
    onSuccess?: () => void;
    /** Fires when the current user's own row moved from `focus` to `blocked`/`waiting` on save. */
    onOwnFocusStalled?: (userPid: string) => void;
}

/**
 * Save mutation + submit handler + submit-ref wiring for the priority form — split out of
 * PriorityForm to keep that file under the repo's line limit.
 */
export function usePriorityFormSubmit({
    form,
    initialData,
    isOwnRowOnly,
    canEditTask,
    canEditOwnRow,
    driName,
    week,
    year,
    onSubmitRef,
    onSubmittingChange,
    onSuccess,
    onOwnFocusStalled,
}: UsePriorityFormSubmitArgs) {
    const { user } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: (values: PriorityFormValues): Promise<unknown> => {
            const assignmentPayload = toAssignmentPayload(values.assignments);
            const resolvedWeek = values.weekNumber ?? week;
            const resolvedYear = values.weekYear ?? year;
            if (initialData?.pid && isOwnRowOnly) {
                // A non-owner may only save their own row, and the task-level PUT is refused whole —
                // so send just that row to the endpoint scoped to the caller.
                const own = values.assignments.find((a) => a.userPid === user?.pid);
                if (!own) return Promise.reject(new Error("Your assignment row was not found."));
                return priorityService.updateOwnAssignment(initialData.pid, {
                    status: own.status,
                    icTask: (own.icTask ?? "").trim(),
                    blockerUserPid: own.blockerUserPid,
                    blockedReason: own.blockedReason?.trim() ? own.blockedReason.trim() : undefined,
                });
            }
            if (initialData?.pid) {
                const body: IUpdatePriority = {
                    name: values.name,
                    type: values.type,
                    status: values.status,
                    description: values.description,
                    weekNumber: resolvedWeek,
                    weekYear: resolvedYear,
                    assignments: assignmentPayload,
                    isConfidential: values.isConfidential,
                    pppPids: values.pppPids,
                };
                return priorityService.updatePriority(initialData.pid, body);
            }
            const body: ICreatePriority = {
                name: values.name,
                type: values.type,
                description: values.description,
                weekNumber: resolvedWeek,
                weekYear: resolvedYear,
                isConfidential: values.isConfidential,
                pppPids: values.pppPids,
            };
            if (assignmentPayload.length > 0) {
                body.assignments = assignmentPayload;
            }
            return priorityService.createPriority(body);
        },
        onSuccess: (_data, values) => {
            queryClient.invalidateQueries({ queryKey: ["priorities"] });
            queryClient.invalidateQueries({ queryKey: ["my-onething"] });
            queryClient.invalidateQueries({ queryKey: ["ppps"] });
            toast({
                title: isOwnRowOnly ? "Your assignment updated" : initialData?.code ? "Priority updated" : "Priority created",
                description: isOwnRowOnly
                    ? "Successfully updated your status and IC task on this priority."
                    : `Successfully ${initialData?.code ? "updated" : "created"} the priority.`,
            });

            // Detect a focus→blocked/waiting transition on the current user's own row (not anyone
            // else's) so the parent can offer to promote a Queue item into the now-empty Focus slot.
            if (user?.pid) {
                const prevStatus = initialData?.assignments?.find((a) => a.assignedUser.pid === user.pid)?.status;
                const nextStatus = values.assignments.find((a) => a.userPid === user.pid)?.status;
                if (prevStatus === "focus" && (nextStatus === "blocked" || nextStatus === "waiting")) {
                    onOwnFocusStalled?.(user.pid);
                }
            }

            onSubmittingChange?.(false);
            onSuccess?.();
        },
        onError: (error) => {
            onSubmittingChange?.(false);
            // Editing your own row out of Focus here is the same release the OneThing page guards;
            // this form has no report composer, so it points at the one place that does.
            const focusMessage = getFocusUpdateRequiredMessage(error, "release");
            // This form is the only place that can send a status change and an assignee row in one
            // save, so it is the only place the contradiction can be raised — and the only place that
            // can explain it.
            const limitMessage =
                focusMessage ??
                // Permission and DRI-report refusals before the mechanical ones: they name a person or an
                // action, where the limit copy only restates the rules.
                getPriorityAuthzMessage(error) ??
                getActiveAssignmentConflictMessage(error) ??
                getAssignmentLimitMessage(error);
            toast({
                title: limitMessage ? "Cannot update status" : "Cannot save priority",
                description: limitMessage ?? "Failed to save priority. Please try again.",
                variant: "destructive",
            });
        },
    });

    // Notify parent of submitting state changes
    useEffect(() => {
        onSubmittingChange?.(mutation.isPending);
    }, [mutation.isPending, onSubmittingChange]);

    const onSubmit = useCallback(
        (values: PriorityFormValues, intent: SubmitIntent = "save") => {
            // Also guarded here, not only by disabling the inputs: a parent dialog drives submission
            // through `onSubmitRef`, so the button that fires this is outside this component's control.
            // A non-owner with their own assignment row still passes — that save goes through
            // PATCH /assignments/me.
            if (!canEditTask && !canEditOwnRow) {
                toast({
                    title: "You cannot edit this task",
                    description: driName
                        ? `Only ${driName} (the DRI), the creator or an admin can change it.`
                        : "This task has no DRI yet — ask its creator or an admin to set one.",
                    variant: "destructive",
                });
                return;
            }
            // Commit gate — client-side only; the server derives candidacy from the same two facts.
            // Only the DRI is checked here: the schema already refuses a save with no PPP at all.
            if (intent === "commit" && !values.assignments.some((a) => a.isDri)) {
                form.setError("assignments", { type: "manual", message: "Pick a DRI to commit" });
                toast({ title: "Not ready to commit", description: COMMIT_GATE_MESSAGE, variant: "destructive" });
                return;
            }
            const currentUserPid = user?.pid;
            if (currentUserPid) {
                const currentUserAssignmentIndex = values.assignments.findIndex(
                    (assignment) => assignment.userPid === currentUserPid
                );
                if (currentUserAssignmentIndex >= 0) {
                    const currentUserIcTask = values.assignments[currentUserAssignmentIndex].icTask?.trim() ?? "";
                    if (!currentUserIcTask) {
                        form.setError(`assignments.${currentUserAssignmentIndex}.icTask`, {
                            type: "manual",
                            message: "IC Task is required for your assignment",
                        });
                        return;
                    }
                }
            }

            // "Save as candidate" has to send no DRI, since that absence is what makes a deliverable a
            // candidate. The people picked stay on as plain assignees; one of them claims it later.
            const stripped = { ...values, assignments: values.assignments.map((a) => ({ ...a, isDri: false })) };
            // Closing or parking a priority cascades its IC rows server-side (D4), so there is nothing
            // to confirm here: the domain owns it for every surface, not just this form.
            mutation.mutate(intent === "candidate" ? stripped : values);
        },
        [form, mutation, user?.pid, canEditTask, canEditOwnRow, driName, toast]
    );

    // Expose submit handler to parent if needed
    useEffect(() => {
        if (onSubmitRef) {
            onSubmitRef((intent) => {
                form.handleSubmit((values) => onSubmit(values, intent))();
            });
        }
    }, [onSubmitRef, form, onSubmit]);

    return { mutation, onSubmit };
}
